import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FaEye, FaTrash, FaCheck, FaTimes, FaTimesCircle, FaDownload, FaUsers } from 'react-icons/fa';

const getEventType = (event) => event?.eventType || event?.event_type || 'deposit';

const TicketManager = () => {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [userStats, setUserStats] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showProofModal, setShowProofModal] = useState(false);
  const [showTicketNumbersModal, setShowTicketNumbersModal] = useState(false);
  const [selectedTicketNumbers, setSelectedTicketNumbers] = useState([]);
  const [showUserStats, setShowUserStats] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const [ticketsRes, eventsRes] = await Promise.all([
        axios.get('/api/tickets/admin', {
          headers: { Authorization: `Bearer ${token}` },
          params: { search: searchTerm || undefined }
        }),
        axios.get('/api/tickets/events/admin', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setTickets(ticketsRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [searchTerm]);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('/api/tickets/admin/stats', {
        headers: { Authorization: `Bearer ${token}` },
        params: { eventId: selectedEvent }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [selectedEvent]);

  const fetchUserStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('/api/tickets/admin/user-stats', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserStats(response.data);
    } catch (error) {
      console.error('Error fetching user stats:', error);
    }
  }, []);

  const handleExportExcel = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('/api/tickets/admin/export', {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `biletler-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert('Excel export hatası');
    }
  };

  const handleClearTicketNumbers = async () => {
    if (!window.confirm('Tüm bilet numaraları temizlensin mi? Bu işlem geri alınamaz.')) {
      return;
    }
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post('/api/tickets/admin/ticket-numbers/clear', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Bilet numaraları temizlendi.');
      fetchData();
    } catch (error) {
      console.error('Error clearing ticket numbers:', error);
      alert(error.response?.data?.error || 'Bilet numaraları temizlenemedi');
    }
  };

  const handleRegenerateTicketNumbers = async () => {
    if (!window.confirm('Bilet numaraları baştan üretilecek. Devam edilsin mi?')) {
      return;
    }
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post('/api/tickets/admin/ticket-numbers/regenerate', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { processedTickets = 0, generatedNumbers = 0 } = response.data || {};
      alert(`Numaralandırma tamamlandı. İşlenen talep: ${processedTickets}, üretilen numara: ${generatedNumbers}`);
      fetchData();
      fetchStats();
    } catch (error) {
      console.error('Error regenerating ticket numbers:', error);
      alert(error.response?.data?.error || 'Bilet numaraları yeniden oluşturulamadı');
    }
  };

  useEffect(() => {
    fetchData();
    fetchStats();
  }, [fetchData, fetchStats]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchData();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, fetchData]);

  const handleStatusChange = async (ticketId, status) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`/api/tickets/${ticketId}/status`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
      fetchStats();
      setShowProofModal(false);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Durum güncellenirken bir hata oluştu');
    }
  };

  const handleDelete = async (ticketId) => {
    if (!window.confirm('Bu bilet talebini silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`/api/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
      fetchStats();
    } catch (error) {
      console.error('Error deleting ticket:', error);
      alert('Bilet silinirken bir hata oluştu');
    }
  };

  const openProofModal = (ticket) => {
    setSelectedTicket(ticket);
    setShowProofModal(true);
  };

  const openTicketNumbersModal = (ticket) => {
    const rawNumbers = ticket.ticketNumbers || ticket.ticket_numbers;
    let parsedNumbers = [];
    if (rawNumbers) {
      try {
        parsedNumbers = JSON.parse(rawNumbers);
      } catch (e) {
        parsedNumbers = [];
      }
    }
    if (!Array.isArray(parsedNumbers) || parsedNumbers.length === 0) {
      const fallbackNumber = ticket.ticketNumber || ticket.ticket_number;
      if (fallbackNumber) {
        parsedNumbers = [fallbackNumber];
      }
    }
    setSelectedTicketNumbers(parsedNumbers);
    setShowTicketNumbersModal(true);
  };

  const depositEvents = events.filter((e) => getEventType(e) !== 'referral');

  const filteredTickets = selectedEvent
    ? tickets.filter(t => t.ticketEventId === parseInt(selectedEvent) || t.ticket_event_id === parseInt(selectedEvent))
    : tickets;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-bold text-gold">Bilet Talepleri</h2>
        <div className="flex gap-3">
          <button
            onClick={handleClearTicketNumbers}
            className="px-4 py-2 bg-red-700 hover:bg-red-800 text-white rounded-lg transition-colors"
          >
            Numaraları Temizle
          </button>
          <button
            onClick={handleRegenerateTicketNumbers}
            className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
          >
            Yeniden Numaralandır
          </button>
          <button
            onClick={() => {
              setShowUserStats(!showUserStats);
              if (!showUserStats) fetchUserStats();
            }}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2"
          >
            <FaUsers />
            {showUserStats ? 'Biletleri Gizle' : 'Kullanıcı İstatistikleri'}
          </button>
          <button
            onClick={handleExportExcel}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center gap-2"
          >
            <FaDownload />
            Excel İndir
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gradient-to-br from-green-500/20 to-blue-500/20 rounded-xl p-6 border border-gold/30">
              <div className="text-white/60 text-sm mb-1">BİLET FİYATI</div>
              <div className="text-green-400 text-3xl font-bold">
                {selectedEvent && depositEvents.find(e => e.id === parseInt(selectedEvent))
                  ? `${depositEvents.find(e => e.id === parseInt(selectedEvent)).ticketPrice || depositEvents.find(e => e.id === parseInt(selectedEvent)).ticket_price}₺`
                  : '-'}
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl p-6 border border-gold/30">
              <div className="text-white/60 text-sm mb-1">TOPLAM SATIN ALIM</div>
              <div className="text-white text-3xl font-bold">{stats.total}</div>
            </div>
            <div className="bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-xl p-6 border border-gold/30">
              <div className="text-white/60 text-sm mb-1">DURUM</div>
              <div className="mt-2">
                <span className="bg-green-500 text-white text-xs px-3 py-1 rounded-full">
                  {stats.approved > 0 ? 'Aktif' : 'Pasif'}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            <div className="bg-gradient-to-br from-blue-600/20 to-green-600/20 rounded-xl p-4 border border-gold/30">
              <div className="text-white/60 text-xs mb-1">TOPLAM</div>
              <div className="text-white text-2xl font-bold">{stats.total}</div>
            </div>
            <div className="bg-gradient-to-br from-blue-600/20 to-yellow-600/20 rounded-xl p-4 border border-gold/30">
              <div className="text-white/60 text-xs mb-1">BEKLEYEN</div>
              <div className="text-yellow-400 text-2xl font-bold">{stats.pending}</div>
            </div>
            <div className="bg-gradient-to-br from-blue-600/20 to-green-600/20 rounded-xl p-4 border border-gold/30">
              <div className="text-white/60 text-xs mb-1">ONAYLANAN</div>
              <div className="text-green-400 text-2xl font-bold">{stats.approved}</div>
            </div>
            <div className="bg-gradient-to-br from-blue-600/20 to-red-600/20 rounded-xl p-4 border border-gold/30">
              <div className="text-white/60 text-xs mb-1">REDDEDİLEN</div>
              <div className="text-red-400 text-2xl font-bold">{stats.rejected}</div>
            </div>
            <div className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 rounded-xl p-4 border border-gold/30">
              <div className="text-white/60 text-xs mb-1">TOPLAM BİLET</div>
              <div className="text-blue-400 text-2xl font-bold">{stats.totalTickets}</div>
            </div>
            <div className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 rounded-xl p-4 border border-gold/30">
              <div className="text-white/60 text-xs mb-1">TOPLAM TUTAR</div>
              <div className="text-green-400 text-2xl font-bold">{stats.totalAmount.toFixed(2)}₺</div>
            </div>
          </div>
        </>
      )}

      {/* User Statistics Section */}
      {showUserStats && (
        <div className="mb-6 bg-gradient-card rounded-xl border border-gold/30 p-6">
          <h3 className="text-2xl font-bold text-gold mb-4">Kullanıcı İstatistikleri</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-navy-800/50">
                <tr>
                  <th className="px-4 py-3 text-left text-white/80 text-sm font-semibold">Kullanıcı</th>
                  <th className="px-4 py-3 text-left text-white/80 text-sm font-semibold">Email</th>
                  <th className="px-4 py-3 text-left text-white/80 text-sm font-semibold">Telegram</th>
                  <th className="px-4 py-3 text-left text-white/80 text-sm font-semibold">Toplam Bilet</th>
                  <th className="px-4 py-3 text-left text-white/80 text-sm font-semibold">Onaylanan</th>
                  <th className="px-4 py-3 text-left text-white/80 text-sm font-semibold">Bekleyen</th>
                  <th className="px-4 py-3 text-left text-white/80 text-sm font-semibold">Reddedilen</th>
                  <th className="px-4 py-3 text-left text-white/80 text-sm font-semibold">Toplam Tutar</th>
                </tr>
              </thead>
              <tbody>
                {userStats.map((user) => (
                  <tr key={user.userId} className="border-t border-gold/10 hover:bg-navy-800/30">
                    <td className="px-4 py-3 text-white text-sm font-semibold">{user.username}</td>
                    <td className="px-4 py-3 text-white/70 text-sm">{user.email || '-'}</td>
                    <td className="px-4 py-3 text-white/70 text-sm">@{user.telegramUsername || '-'}</td>
                    <td className="px-4 py-3 text-white text-sm font-bold">{user.totalTickets}</td>
                    <td className="px-4 py-3 text-green-400 text-sm font-semibold">{user.approvedTickets}</td>
                    <td className="px-4 py-3 text-yellow-400 text-sm font-semibold">{user.pendingTickets}</td>
                    <td className="px-4 py-3 text-red-400 text-sm font-semibold">{user.rejectedTickets}</td>
                    <td className="px-4 py-3 text-green-400 text-sm font-semibold">{user.totalAmount.toFixed(2)}₺</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 flex gap-4">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Kullanıcı, site veya Telegram ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-3 pl-10 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
          />
          <span className="absolute left-3 top-3.5 text-white/60">🔍</span>
        </div>
        <select
          value={selectedEvent || 'all'}
          onChange={(e) => setSelectedEvent(e.target.value === 'all' ? null : e.target.value)}
          className="px-4 py-3 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
        >
          <option value="all">Tüm Etkinlikler</option>
          {depositEvents.map(event => (
            <option key={event.id} value={event.id}>{event.title}</option>
          ))}
        </select>
      </div>

      {/* Tickets Table */}
      <div className="bg-gradient-card rounded-xl border border-gold/30 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-navy-800/50">
              <tr>
                <th className="px-4 py-3 text-left text-white/80 text-sm font-semibold">TARİH</th>
                <th className="px-4 py-3 text-left text-white/80 text-sm font-semibold">KULLANICI</th>
                <th className="px-4 py-3 text-left text-white/80 text-sm font-semibold">YATIRIM</th>
                <th className="px-4 py-3 text-left text-white/80 text-sm font-semibold">BİLET</th>
                <th className="px-4 py-3 text-left text-white/80 text-sm font-semibold">SİTE</th>
                <th className="px-4 py-3 text-left text-white/80 text-sm font-semibold">DURUM</th>
                <th className="px-4 py-3 text-left text-white/80 text-sm font-semibold">İŞLEMLER</th>
              </tr>
            </thead>
            <tbody>
              {filteredTickets.map((ticket) => {
                const user = ticket.user || {};
                const createdAt = ticket.createdAt || ticket.created_at;
                const date = createdAt ? new Date(createdAt).toLocaleString('tr-TR') : '-';
                
                return (
                  <tr key={ticket.id} className="border-t border-gold/10 hover:bg-navy-800/30">
                    <td className="px-4 py-3 text-white/70 text-sm">{date}</td>
                    <td className="px-4 py-3">
                      <div className="text-white text-sm">
                        <div className="font-semibold">{user.username || '-'}</div>
                        <div className="text-white/60 text-xs">{user.email || '-'}</div>
                        <div className="text-white/60 text-xs">Telegram: @{user.telegramUsername || user.telegram_username || '-'}</div>
                        <div className="text-white/60 text-xs">Site Kullanıcı Adı: {ticket.siteUsername || ticket.site_username || '-'}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white text-sm font-semibold">
                      {ticket.depositAmount || ticket.deposit_amount || 0}₺
                    </td>
                    <td className="px-4 py-3 text-white text-sm">
                      {ticket.ticketCount || ticket.ticket_count || 0} adet
                    </td>
                    <td className="px-4 py-3 text-white text-sm">
                      <div className="font-semibold">{ticket.sponsorName || ticket.sponsor_name || 'Bilinmiyor'}</div>
                      <div className="text-white/60 text-xs">Kullanıcı: {ticket.siteUsername || ticket.site_username || '-'}</div>
                    </td>
                    <td className="px-4 py-3">
                      {ticket.status === 'approved' && (
                        <span className="bg-green-500 text-white text-xs px-3 py-1 rounded-full">Onaylandı</span>
                      )}
                      {ticket.status === 'pending' && (
                        <span className="bg-yellow-500 text-white text-xs px-3 py-1 rounded-full">Bekliyor</span>
                      )}
                      {ticket.status === 'rejected' && (
                        <span className="bg-red-500 text-white text-xs px-3 py-1 rounded-full">Reddedildi</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openProofModal(ticket)}
                          className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                          title="Kanıt Görseli"
                        >
                          <FaEye />
                        </button>
                        <button
                          onClick={() => openTicketNumbersModal(ticket)}
                          className="p-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-xs font-bold"
                          title="Bilet Numaraları"
                        >
                          #
                        </button>
                        <button
                          onClick={() => handleDelete(ticket.id)}
                          className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                          title="Sil"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Proof Image Modal */}
      {showProofModal && selectedTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-gradient-card rounded-xl p-6 border border-gold/30 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gold">Kanıt Görseli</h3>
              <button
                onClick={() => setShowProofModal(false)}
                className="text-white/60 hover:text-white"
              >
                <FaTimesCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-4">
              <img
                src={selectedTicket.proofImageUrl || selectedTicket.proof_image_url}
                alt="Kanıt Görseli"
                className="w-full max-w-2xl mx-auto max-h-[320px] object-contain rounded-lg border border-gold/30"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4 text-sm">
              <div>
                <div className="text-white/60">Kullanıcı</div>
                <div className="text-white font-semibold">
                  {selectedTicket.user?.username || '-'}
                </div>
              </div>
              <div>
                <div className="text-white/60">E-posta</div>
                <div className="text-white font-semibold break-all">
                  {selectedTicket.user?.email || '-'}
                </div>
              </div>
              <div>
                <div className="text-white/60">Telegram</div>
                <div className="text-white font-semibold break-all">
                  {(() => {
                    const u =
                      selectedTicket.user?.telegramUsername ||
                      selectedTicket.user?.telegram_username;
                    if (!u) return '—';
                    const handle = String(u).replace(/^@/, '');
                    return (
                      <a
                        href={`https://t.me/${handle}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-navy-300 hover:text-navy-200 underline-offset-2 hover:underline"
                      >
                        @{handle}
                      </a>
                    );
                  })()}
                </div>
              </div>
              <div>
                <div className="text-white/60">Site kullanıcı adı</div>
                <div className="text-white font-semibold">
                  {selectedTicket.siteUsername || selectedTicket.site_username || '-'}
                </div>
              </div>
              <div>
                <div className="text-white/60">Yatırım tutarı</div>
                <div className="text-white font-semibold">
                  {selectedTicket.depositAmount || selectedTicket.deposit_amount || 0}₺
                </div>
              </div>
              <div>
                <div className="text-white/60">Bilet sayısı</div>
                <div className="text-white font-semibold">
                  {selectedTicket.ticketCount || selectedTicket.ticket_count || 0}
                </div>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              {selectedTicket.status === 'pending' && (
                <>
                  <button
                    onClick={() => handleStatusChange(selectedTicket.id, 'rejected')}
                    className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    <FaTimes />
                    Reddet
                  </button>
                  <button
                    onClick={() => handleStatusChange(selectedTicket.id, 'approved')}
                    className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    <FaCheck />
                    Onayla
                  </button>
                </>
              )}
              <button
                onClick={() => setShowProofModal(false)}
                className="px-6 py-2 bg-navy-700 hover:bg-navy-600 text-white rounded-lg transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ticket Numbers Modal */}
      {showTicketNumbersModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-gradient-card rounded-xl p-6 border border-gold/30 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gold">Bilet Numaraları</h3>
              <button
                onClick={() => setShowTicketNumbersModal(false)}
                className="text-white/60 hover:text-white"
              >
                <FaTimesCircle className="w-6 h-6" />
              </button>
            </div>

            {selectedTicketNumbers.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {selectedTicketNumbers.map((num, idx) => (
                  <span
                    key={`${num}-${idx}`}
                    className="bg-gold/20 text-gold px-3 py-1 rounded-full text-sm font-semibold"
                  >
                    {num}
                  </span>
                ))}
              </div>
            ) : (
              <div className="text-white/70">Numara bulunamadı.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketManager;
